import React, { useCallback, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
    getShipment, getCarrierEvents, getPartnerEvents, getLabelMeta, getLabelBlob, getSummary,
    agreementView, AGREEMENT, SIDE_KEYS,
    txt, money, dt, ago,
    TH, TD, BTN,
    Card, Alert, EmptyRow, Spinner, StatusPill, RawPill, UnknownPill, ReadOnlyBadge,
} from './forwardingApi';

// ─────────────────────────────────────────────────────────────────────────────
// Forwarded Orders — detail.  READ-ONLY.
//
// THE POINT OF THIS SCREEN is the three-way status comparison: what OUR system
// thinks, what the PARTNER was last told, and what the CARRIER last told us.
// Those three genuinely diverge in production right now (our legs sit at
// RECEIVED while the carrier says Canceled), and a screen that smooths that into
// apparent agreement is worse than no screen at all.
//
// So the vocabulary here is the SERVER'S, letter for letter:
// ForwardingStatusTrioDto.agreement is "AGREE", "DIVERGENT" or
// "INSUFFICIENT_DATA" — see ForwardingAdminService.compareSides(). Any other
// value is rendered as unrecognised, never coerced into one of the three.
//
// Three further rules this file keeps:
//   * UNKNOWN IS A VALUE. When a side has said nothing, it is drawn as unknown
//     with the server's own unknownReason. It is never filled in from another
//     side, and a side that could not be FETCHED is drawn differently again.
//   * COMPARE WHAT THE SERVER COMPARED. The carrier's comparable value is its
//     MAPPED status, not its raw word — the raw word is the carrier's own
//     vocabulary and is displayed, but never compared.
//   * NO WRITES. Retry, approve, cancel and manual handover are gone. A retry
//     re-queues a forward, and a forward creates a real, billable shipment at
//     the carrier; this app has no working route-level authorisation to stand in
//     front of that. Reads only.
//
// Every fetch owns its own loading flag, clears it in a finally, and surfaces
// its own error. Nothing shares a catch and nothing swallows one.
// ─────────────────────────────────────────────────────────────────────────────

const TABS = [
    { key: 'overview', label: 'Overview', icon: 'fa-list-alt' },
    { key: 'attempts', label: 'Attempts', icon: 'fa-repeat' },
    { key: 'carrier', label: 'Carrier events', icon: 'fa-truck' },
    { key: 'partner', label: 'Partner events', icon: 'fa-paper-plane' },
    { key: 'label', label: 'Label', icon: 'fa-barcode' },
];

export default function ForwardedShipmentDetail({ shipmentId }) {
    const navigate = useNavigate();
    const id = shipmentId;

    const [tab, setTab] = useState('overview');

    // ── detail ───────────────────────────────────────────────────────────────
    const [detail, setDetail] = useState(null);
    const [detailLoading, setDetailLoading] = useState(true);
    const [detailError, setDetailError] = useState('');

    // ── carrier events (inbound) ─────────────────────────────────────────────
    const [carrierEvents, setCarrierEvents] = useState([]);
    const [carrierEventsLoading, setCarrierEventsLoading] = useState(true);
    const [carrierEventsLoaded, setCarrierEventsLoaded] = useState(false);
    const [carrierEventsError, setCarrierEventsError] = useState('');

    // ── partner events (outbound webhook deliveries) ─────────────────────────
    const [partnerEvents, setPartnerEvents] = useState(null);
    const [partnerEventsLoading, setPartnerEventsLoading] = useState(true);
    const [partnerEventsLoaded, setPartnerEventsLoaded] = useState(false);
    const [partnerEventsError, setPartnerEventsError] = useState('');

    // ── label metadata (never bytes) ─────────────────────────────────────────
    // NOT loaded on mount, unlike everything else. When no label is stored, the
    // main backend answers this by making a live (read-only) label GET to the
    // carrier for an existing waybill. It books nothing, but it is still an
    // outbound carrier call, and firing one every time somebody opens a leg is
    // not something a status screen should do behind the operator's back.
    const [labelMeta, setLabelMeta] = useState(null);
    const [labelMetaLoading, setLabelMetaLoading] = useState(false);
    const [labelMetaLoaded, setLabelMetaLoaded] = useState(false);
    const [labelMetaError, setLabelMetaError] = useState('');

    // ── label bytes, fetched on demand ───────────────────────────────────────
    const [labelUrl, setLabelUrl] = useState('');
    const [labelBlobLoading, setLabelBlobLoading] = useState(false);
    const [labelBlobError, setLabelBlobError] = useState('');

    // ── carrier summary ──────────────────────────────────────────────────────
    const [summary, setSummary] = useState(null);
    const [summaryLoading, setSummaryLoading] = useState(false);
    const [summaryError, setSummaryError] = useState('');

    const shipment = (detail && detail.shipment) || {};
    const trio = (detail && detail.statusTrio) || null;
    const viewer = (detail && detail.viewer) || null;
    const attempts = (detail && Array.isArray(detail.attempts)) ? detail.attempts : [];
    const consignee = (detail && detail.consignee) || null;
    const carrierCode = shipment.carrierCode || '';

    // ── loaders ──────────────────────────────────────────────────────────────

    const loadDetail = useCallback(async () => {
        setDetailLoading(true);
        setDetailError('');
        try {
            const data = await getShipment(id);
            setDetail(data || null);
            if (!data) setDetailError('The forwarding service returned no detail for this leg.');
        } catch (e) {
            setDetail(null);
            setDetailError((e && e.message) || 'Could not load this forwarded leg');
        } finally {
            setDetailLoading(false);
        }
    }, [id]);

    const loadCarrierEvents = useCallback(async () => {
        setCarrierEventsLoading(true);
        setCarrierEventsError('');
        try {
            const data = await getCarrierEvents(id);
            setCarrierEvents(Array.isArray(data) ? data : []);
        } catch (e) {
            setCarrierEvents([]);
            setCarrierEventsError((e && e.message) || 'Could not load carrier events');
        } finally {
            setCarrierEventsLoading(false);
            setCarrierEventsLoaded(true);
        }
    }, [id]);

    const loadPartnerEvents = useCallback(async () => {
        setPartnerEventsLoading(true);
        setPartnerEventsError('');
        try {
            const data = await getPartnerEvents(id);
            setPartnerEvents(data || null);
        } catch (e) {
            setPartnerEvents(null);
            setPartnerEventsError((e && e.message) || 'Could not load partner deliveries');
        } finally {
            setPartnerEventsLoading(false);
            setPartnerEventsLoaded(true);
        }
    }, [id]);

    const loadLabelMeta = useCallback(async () => {
        setLabelMetaLoading(true);
        setLabelMetaError('');
        try {
            const data = await getLabelMeta(id);
            setLabelMeta(data || null);
        } catch (e) {
            setLabelMeta(null);
            setLabelMetaError((e && e.message) || 'Could not check whether a label exists');
        } finally {
            setLabelMetaLoading(false);
            setLabelMetaLoaded(true);
        }
    }, [id]);

    // Three independent fetches on mount, allSettled so one failing panel cannot
    // blank the other two. The label is deliberately not among them.
    const loadAll = useCallback(() => {
        Promise.allSettled([loadDetail(), loadCarrierEvents(), loadPartnerEvents()]);
    }, [loadDetail, loadCarrierEvents, loadPartnerEvents]);

    useEffect(() => { if (id) loadAll(); }, [id, loadAll]);

    // A new shipment id must forget the previous leg's label entirely.
    useEffect(() => {
        setLabelMeta(null);
        setLabelMetaLoaded(false);
        setLabelMetaError('');
        setLabelUrl('');
        setLabelBlobError('');
    }, [id]);

    // First visit to the Label tab is what triggers the label lookup.
    useEffect(() => {
        if (tab === 'label' && !labelMetaLoaded && !labelMetaLoading) loadLabelMeta();
    }, [tab, labelMetaLoaded, labelMetaLoading, loadLabelMeta]);

    // Summary needs the carrier code, which only the detail knows.
    useEffect(() => {
        if (!carrierCode) {
            setSummary(null);
            setSummaryError('');
            return undefined;
        }
        let alive = true;
        setSummaryLoading(true);
        setSummaryError('');
        (async () => {
            try {
                const data = await getSummary(carrierCode);
                if (alive) setSummary(data || null);
            } catch (e) {
                if (alive) {
                    setSummary(null);
                    setSummaryError((e && e.message) || 'Could not load the carrier summary');
                }
            } finally {
                if (alive) setSummaryLoading(false);
            }
        })();
        return () => { alive = false; };
    }, [carrierCode]);

    // Release the object URL so the blob is not leaked when leaving the page.
    useEffect(() => () => { if (labelUrl) URL.revokeObjectURL(labelUrl); }, [labelUrl]);

    const viewLabel = async () => {
        setLabelBlobLoading(true);
        setLabelBlobError('');
        try {
            const blob = await getLabelBlob(id);
            const type = (labelMeta && labelMeta.contentType) || (blob && blob.type) || 'application/pdf';
            const url = URL.createObjectURL(new Blob([blob], { type }));
            // The effect above revokes the previous object URL when this changes,
            // and revokes the current one on unmount.
            setLabelUrl(url);
        } catch (e) {
            setLabelBlobError((e && e.message) || 'Could not load the label file');
        } finally {
            setLabelBlobLoading(false);
        }
    };

    // ── render ───────────────────────────────────────────────────────────────

    if (detailLoading && !detail) {
        return (
            <div style={PAGE}>
                <BackLink navigate={navigate} />
                <Card title="Forwarded leg"><Spinner label="Loading forwarding detail…" /></Card>
            </div>
        );
    }

    if (detailError && !detail) {
        return (
            <div style={PAGE}>
                <BackLink navigate={navigate} />
                <Card title={`Forwarded leg #${txt(id)}`}>
                    <Alert>{detailError}</Alert>
                    <div style={{ marginTop: 12 }}>
                        <button onClick={loadAll} style={BTN.primary}><i className="fa fa-refresh" /> Try again</button>
                    </div>
                </Card>
            </div>
        );
    }

    return (
        <div style={PAGE}>
            <BackLink navigate={navigate} />

            {/* ── Header ── */}
            <Card>
                <div style={{ display: 'flex', flexWrap: 'wrap', alignItems: 'flex-start', gap: 16, justifyContent: 'space-between' }}>
                    <div>
                        <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap' }}>
                            <h2 style={{ margin: 0, fontSize: 20, color: '#0f172a' }}>{txt(shipment.legBookingId, 'leg reference unknown')}</h2>
                            <StatusPill status={shipment.status} />
                            {shipment.isTerminal === true ? <Tag>terminal</Tag> : null}
                            {shipment.holdReason ? <Tag tone="warn">held</Tag> : null}
                        </div>
                        <div style={{ fontSize: 12, color: '#64748b', marginTop: 6, display: 'flex', gap: 14, flexWrap: 'wrap' }}>
                            <span><strong>Carrier:</strong> {txt(shipment.carrierCode)}</span>
                            <span><strong>Partner:</strong> {txt(shipment.partnerName)}{shipment.fkPartnerId != null ? ` (#${shipment.fkPartnerId})` : ''}</span>
                            <span><strong>Order ref:</strong> {txt(shipment.orderBookingId)}</span>
                            <span><strong>Business ref:</strong> {txt(shipment.businessRefNo)}</span>
                            <span><strong>Carrier AWB:</strong> {shipment.carrierHawb ? <code>{txt(shipment.carrierHawb)}</code> : <em style={{ color: '#94a3b8' }}>none yet</em>}</span>
                        </div>
                        {shipment.holdReason && (
                            <div style={{ marginTop: 8, fontSize: 12, color: '#92400e', background: '#fef3c7', borderRadius: 6, padding: '6px 10px', display: 'inline-block' }}>
                                <i className="fa fa-pause-circle" style={{ marginRight: 6 }} />
                                Hold reason: {txt(shipment.holdReason)}
                            </div>
                        )}
                        {shipment.lastErrorCode && (
                            <div style={{ marginTop: 8, fontSize: 12, color: '#991b1b', background: '#fee2e2', borderRadius: 6, padding: '6px 10px' }}>
                                <i className="fa fa-exclamation-triangle" style={{ marginRight: 6 }} />
                                Last error <strong>{txt(shipment.lastErrorCode)}</strong> — {txt(shipment.lastErrorMsg, 'no message returned')}
                            </div>
                        )}
                    </div>
                    <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end', gap: 8 }}>
                        <ReadOnlyBadge email={viewer && viewer.email} />
                        <button onClick={loadAll} disabled={detailLoading} style={detailLoading ? BTN.disabled : BTN.ghost}>
                            <i className={`fa fa-refresh ${detailLoading ? 'fa-spin' : ''}`} /> Reload
                        </button>
                    </div>
                </div>

                {detailError && <Alert tone="warn">The last reload failed: {detailError}. What you see below may be stale.</Alert>}

                <div style={{ marginTop: 14, paddingTop: 14, borderTop: '1px solid #f1f5f9', fontSize: 12, color: '#64748b' }}>
                    <i className="fa fa-lock" style={{ marginRight: 6 }} />
                    This console is read-only. Forwarding a leg creates a real, billable shipment at the carrier, so retry,
                    approve, cancel and manual handover are not available here — they are done from the main backend’s own
                    admin console, which authorises them.
                </div>
            </Card>

            {/* ── The whole reason this screen exists ── */}
            <StatusTrio trio={trio} loading={detailLoading} />

            {/* ── Carrier snapshot ── */}
            {carrierCode && (
                <Card title={`${txt(carrierCode)} — carrier snapshot`}>
                    {summaryLoading && <Spinner label="Loading carrier summary…" />}
                    {!summaryLoading && summaryError && <Alert tone="warn">Could not load the carrier summary: {summaryError}</Alert>}
                    {!summaryLoading && !summaryError && !summary && (
                        <div style={EMPTY}>No summary was returned for this carrier.</div>
                    )}
                    {!summaryLoading && !summaryError && summary && (
                        <div style={{ display: 'flex', gap: 20, flexWrap: 'wrap', fontSize: 12, color: '#475569' }}>
                            <span><strong>Forwarded in total:</strong> {numOr(summary.totalShipments)}</span>
                            <span><strong>Needs action:</strong> {numOr(summary.needsActionCount)}</span>
                            <span><strong>Requests succeeded:</strong> {numOr(summary.requestsSucceeded)}</span>
                            <span><strong>Requests failed:</strong> {numOr(summary.requestsFailed)}</span>
                            <span><strong>Requests in flight:</strong> {numOr(summary.requestsInFlight)}</span>
                            <span><strong>Last failure:</strong> {dt(summary.lastFailureAt)}</span>
                        </div>
                    )}
                </Card>
            )}

            {/* ── Tabs ── */}
            <div style={{ display: 'flex', gap: 4, marginBottom: 12, flexWrap: 'wrap' }}>
                {TABS.map((t) => {
                    const active = tab === t.key;
                    // A count is shown only when that panel actually loaded. A "0"
                    // on a panel that failed would read as "there are none".
                    let badge = null;
                    if (t.key === 'attempts' && detail && !detailError) badge = attempts.length;
                    if (t.key === 'carrier' && carrierEventsLoaded && !carrierEventsError) badge = carrierEvents.length;
                    if (t.key === 'partner' && partnerEventsLoaded && !partnerEventsError) {
                        badge = (partnerEvents && Array.isArray(partnerEvents.events)) ? partnerEvents.events.length : 0;
                    }
                    return (
                        <button
                            key={t.key}
                            onClick={() => setTab(t.key)}
                            style={{
                                display: 'inline-flex', alignItems: 'center', gap: 6, padding: '8px 14px',
                                borderRadius: '8px 8px 0 0', fontSize: 13, fontWeight: active ? 600 : 500, cursor: 'pointer',
                                border: '1px solid #e2e8f0', borderBottom: active ? '1px solid #fff' : '1px solid #e2e8f0',
                                background: active ? '#fff' : '#f1f5f9', color: active ? '#0f172a' : '#64748b',
                            }}
                        >
                            <i className={`fa ${t.icon}`} />
                            {t.label}
                            {badge != null && <span style={{ fontSize: 10, color: '#94a3b8' }}>({badge})</span>}
                        </button>
                    );
                })}
            </div>

            {tab === 'overview' && (
                <OverviewTab
                    shipment={shipment}
                    consignee={consignee}
                    consigneeRestricted={detail && detail.consigneeRestricted === true}
                    consigneeNote={detail && detail.consigneeRestrictedNote}
                />
            )}
            {tab === 'attempts' && (
                <AttemptsTab attempts={attempts} loading={detailLoading} error={detailError} />
            )}
            {tab === 'carrier' && (
                <CarrierEventsTab
                    events={carrierEvents} loading={carrierEventsLoading}
                    error={carrierEventsError} onReload={loadCarrierEvents}
                />
            )}
            {tab === 'partner' && (
                <PartnerEventsTab
                    payload={partnerEvents} loading={partnerEventsLoading}
                    error={partnerEventsError} onReload={loadPartnerEvents}
                />
            )}
            {tab === 'label' && (
                <LabelTab
                    meta={labelMeta} metaLoading={labelMetaLoading} metaLoaded={labelMetaLoaded}
                    metaError={labelMetaError} onLoadMeta={loadLabelMeta}
                    url={labelUrl} blobLoading={labelBlobLoading} blobError={labelBlobError}
                    onView={viewLabel}
                />
            )}
        </div>
    );
}

// ── Three-way status ────────────────────────────────────────────────────────

/**
 * Which field of each side the SERVER compared. Copied from
 * ForwardingAdminService.compareSides(): ours and partner are compared on their
 * status, the carrier on its MAPPED status — its raw word is its own vocabulary
 * and is never compared against ours.
 */
const comparableValue = (key, side) => {
    if (!side || side.known !== true) return null;
    if (key === SIDE_KEYS.CARRIER) return side.mappedStatus != null ? side.mappedStatus : null;
    return side.status != null ? side.status : null;
};

function StatusTrio({ trio, loading }) {
    if (!trio) {
        return (
            <Card title="Three-way status">
                <div style={{ padding: 16, background: '#f1f5f9', borderRadius: 8, color: '#334155', fontSize: 13, lineHeight: 1.55 }}>
                    <i className="fa fa-question-circle" style={{ marginRight: 6 }} />
                    {loading
                        ? 'Loading the status comparison…'
                        : (
                            <>
                                The forwarding service returned no status comparison for this leg.
                                <strong> This is not the same as the three sides agreeing</strong> — nothing was compared.
                            </>
                        )}
                </div>
            </Card>
        );
    }

    const view = agreementView(trio.agreement);
    const compared = Array.isArray(trio.comparedSides) ? trio.comparedSides : [];
    const isComparedSide = (key) => compared.some((c) => String(c).toUpperCase() === key);
    const divergent = view.value === AGREEMENT.DIVERGENT;

    return (
        <Card
            title="Three-way status"
            subtitle="What our system thinks, what the partner was last told, and what the carrier last told us."
            style={divergent ? { border: '2px solid #dc2626' } : undefined}
        >
            <div style={{
                background: view.bg, color: view.fg, borderRadius: 8, padding: '14px 16px',
                fontSize: 13, lineHeight: 1.55, borderLeft: `6px solid ${view.border}`,
            }}>
                <div style={{ fontWeight: 700, fontSize: divergent ? 15 : 13, marginBottom: 4 }}>
                    <i className={`fa ${view.icon}`} style={{ marginRight: 8 }} />
                    {view.head}
                </div>

                {!view.known && (
                    <div style={{ marginBottom: 6 }}>
                        The server sent <code>{view.value}</code>, which is not one of AGREE / DIVERGENT /
                        INSUFFICIENT_DATA. It is shown as-is rather than guessed at — do not read it as agreement.
                    </div>
                )}

                {/* The server's own sentence naming what disagrees. */}
                {trio.agreementNote
                    ? <div style={{ fontWeight: divergent ? 600 : 400 }}>{txt(trio.agreementNote)}</div>
                    : <div style={{ opacity: 0.8 }}>The server gave no note explaining this result.</div>}

                {divergent && (
                    <div style={{ marginTop: 8, fontSize: 12 }}>
                        These are different systems describing one parcel. Until they are reconciled, at least one of the
                        carrier, the partner and this database is acting on a status that is not true.
                    </div>
                )}

                <div style={{ marginTop: 8, fontSize: 11, opacity: 0.9 }}>
                    {compared.length > 0
                        ? <>Compared: <strong>{compared.map((c) => txt(c)).join(' vs ')}</strong>.</>
                        : <>No two sides had a comparable status, so nothing was compared.</>}
                    {trio.stalestSide ? <> Oldest information: <strong>{txt(trio.stalestSide)}</strong>.</> : null}
                    {trio.partnerLinkExact === false ? (
                        <> The partner column is matched by <strong>{txt(trio.partner && trio.partner.matchedBy, 'reference')}</strong>,
                            not by a foreign key — see the Partner events tab for exactly which references were tried.</>
                    ) : null}
                </div>
            </div>

            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(270px, 1fr))', gap: 12, marginTop: 14 }}>
                <SideCard
                    sideKey={SIDE_KEYS.OURS} fallbackTitle="Our system" side={trio.ours}
                    stale={trio.stalestSide === SIDE_KEYS.OURS}
                    compared={isComparedSide(SIDE_KEYS.OURS)} divergent={divergent}
                />
                <SideCard
                    sideKey={SIDE_KEYS.PARTNER} fallbackTitle="Partner" side={trio.partner}
                    stale={trio.stalestSide === SIDE_KEYS.PARTNER}
                    compared={isComparedSide(SIDE_KEYS.PARTNER)} divergent={divergent}
                />
                <SideCard
                    sideKey={SIDE_KEYS.CARRIER} fallbackTitle="Carrier" side={trio.carrier}
                    stale={trio.stalestSide === SIDE_KEYS.CARRIER}
                    compared={isComparedSide(SIDE_KEYS.CARRIER)} divergent={divergent}
                />
            </div>
        </Card>
    );
}

/**
 * One side of the trio. FOUR visually distinct states, never collapsed into
 * fewer:
 *   compared + divergent → red card, the value it was compared as
 *   compared + agreeing  → plain card, the value it was compared as
 *   known but not compared → plain card saying it took no part in the comparison
 *   unknown / absent     → dashed grey card carrying the server's reason
 */
function SideCard({ sideKey, fallbackTitle, side, stale, compared, divergent }) {
    if (!side) {
        return (
            <div style={{ border: '1px dashed #cbd5e1', background: '#f8fafc', borderRadius: 10, padding: 14 }}>
                <CardHead title={fallbackTitle} />
                <div style={{ marginTop: 8 }}><UnknownPill label="not returned" /></div>
                <div style={{ fontSize: 12, color: '#64748b', marginTop: 8 }}>
                    The forwarding service did not return this side at all. Treat it as unknown, not as agreement.
                </div>
            </div>
        );
    }

    const known = side.known === true;
    const value = comparableValue(sideKey, side);
    const loud = compared && divergent;

    return (
        <div style={{
            border: loud ? '2px solid #dc2626' : (known ? '1px solid #e2e8f0' : '1px dashed #cbd5e1'),
            background: loud ? '#fff5f5' : (known ? '#fff' : '#f8fafc'),
            borderRadius: 10, padding: 14,
        }}>
            <CardHead title={txt(side.label, fallbackTitle)} stale={stale} />

            {known ? (
                <>
                    <div style={{ marginTop: 8, display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
                        {sideKey === SIDE_KEYS.CARRIER
                            ? <RawPill value={side.status} title="the carrier's own word for this shipment" />
                            : <StatusPill status={side.status} />}
                        {sideKey === SIDE_KEYS.CARRIER && (
                            side.mappedStatus
                                ? (
                                    <span style={{ fontSize: 12, color: '#475569' }}>
                                        → mapped to <StatusPill status={side.mappedStatus} />
                                    </span>
                                )
                                : (
                                    <span style={{ fontSize: 12, color: '#b45309' }}>
                                        → <strong>unmapped</strong>, so nothing was written to our leg from it
                                    </span>
                                )
                        )}
                    </div>
                    {side.reason ? <div style={{ fontSize: 12, color: '#475569', marginTop: 8 }}>{txt(side.reason)}</div> : null}
                </>
            ) : (
                <div style={{ marginTop: 8 }}>
                    <UnknownPill />
                    <div style={{ fontSize: 12, color: '#64748b', marginTop: 8 }}>
                        {txt(side.unknownReason, 'The server gave no reason for why this side is unknown.')}
                    </div>
                </div>
            )}

            {/* What this side contributed to the comparison — stated, never implied. */}
            <div style={{
                marginTop: 10, padding: '6px 9px', borderRadius: 6, fontSize: 11,
                background: loud ? '#fee2e2' : '#f8fafc',
                color: loud ? '#7f1d1d' : '#64748b',
                border: `1px solid ${loud ? '#fecaca' : '#e2e8f0'}`,
            }}>
                {compared && value != null && (
                    <>
                        Compared as <strong>{txt(value)}</strong>
                        {sideKey === SIDE_KEYS.CARRIER ? ' (the mapped status, not the carrier’s own word)' : ''}
                    </>
                )}
                {compared && value == null && (
                    <>The server listed this side as compared but returned no value for it — treat the result above with care.</>
                )}
                {!compared && (
                    <>Not part of the comparison{known ? ' — it has no comparable status' : ' — this side is unknown'}.</>
                )}
            </div>

            <dl style={{ margin: '12px 0 0', fontSize: 11, color: '#64748b', display: 'grid', gridTemplateColumns: 'auto 1fr', gap: '4px 10px' }}>
                <dt style={{ fontWeight: 600 }}>As of</dt>
                <dd style={{ margin: 0 }}>
                    {dt(side.asOf)}{side.asOf ? ` (${ago(side.asOf)})` : ''}
                    {side.asOfMeaning ? <div style={{ color: '#94a3b8' }}>{txt(side.asOfMeaning)}</div> : null}
                </dd>
                <dt style={{ fontWeight: 600 }}>Source</dt>
                <dd style={{ margin: 0 }}>{txt(side.source)}</dd>
                {side.eventCount != null && (
                    <>
                        <dt style={{ fontWeight: 600 }}>Deliveries</dt>
                        <dd style={{ margin: 0 }}>{Number(side.eventCount)}</dd>
                    </>
                )}
                {side.matchedBy && (
                    <>
                        <dt style={{ fontWeight: 600 }}>Matched by</dt>
                        <dd style={{ margin: 0 }}>{txt(side.matchedBy)}</dd>
                    </>
                )}
                {side.deliveryOk != null && (
                    <>
                        <dt style={{ fontWeight: 600 }}>Delivery</dt>
                        <dd style={{ margin: 0 }}>
                            {side.deliveryOk === true ? 'the partner accepted it' : 'the partner did not accept it'}
                            {side.deliveryHttpStatus != null ? ` · HTTP ${Number(side.deliveryHttpStatus)}` : ''}
                            {side.deliveryRetryStatus ? ` · ${txt(side.deliveryRetryStatus)}` : ''}
                        </dd>
                    </>
                )}
                {side.attemptedStatus && (
                    <>
                        <dt style={{ fontWeight: 600 }}>Last attempted</dt>
                        <dd style={{ margin: 0 }}>
                            {txt(side.attemptedStatus)}{side.attemptedAt ? ` at ${dt(side.attemptedAt)}` : ''}
                            <div style={{ color: '#94a3b8' }}>what we tried to tell them, which is not what they know</div>
                        </dd>
                    </>
                )}
            </dl>
        </div>
    );
}

function CardHead({ title, stale }) {
    return (
        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            <span style={{ fontSize: 11, fontWeight: 700, color: '#0f172a', textTransform: 'uppercase', letterSpacing: '.06em' }}>{title}</span>
            {stale ? <span style={{ fontSize: 10, fontWeight: 600, color: '#b45309', background: '#fef3c7', borderRadius: 8, padding: '1px 7px' }}>oldest</span> : null}
        </div>
    );
}

// ── Tabs ────────────────────────────────────────────────────────────────────

function OverviewTab({ shipment, consignee, consigneeRestricted, consigneeNote }) {
    const s = shipment || {};
    return (
        <>
            <Card title="Shipment">
                <Grid rows={[
                    ['Shipment id', txt(s.id)],
                    ['Leg reference', txt(s.legBookingId)],
                    ['Order id', txt(s.fkOrderId)],
                    ['Task id (leg)', txt(s.fkTaskId)],
                    ['Order booking id', txt(s.orderBookingId)],
                    ['Business ref', txt(s.businessRefNo)],
                    ['Job type', txt(s.jobType)],
                    ['Partner', `${txt(s.partnerName)}${s.fkPartnerId != null ? ` (#${s.fkPartnerId})` : ''}`],
                    ['Carrier', txt(s.carrierCode)],
                    ['Mode', txt(s.mode)],
                    ['Direction', txt(s.direction)],
                    ['Movement type', txt(s.movementType)],
                    ['Origin → service country', `${txt(s.originCountry)} → ${txt(s.serviceCountry)}`],
                    ['Delivery location type', txt(s.deliveryLocationType)],
                    ['Pieces', s.pieceCount != null ? Number(s.pieceCount) : '—'],
                ]} />
            </Card>

            <Card title="Carrier references">
                <Grid rows={[
                    ['Carrier AWB (HAWB)', txt(s.carrierHawb, 'none yet')],
                    ['Carrier shipment id', txt(s.carrierShipmentId)],
                    ['Tracking url', txt(s.carrierTrackingUrl)],
                    ['Label format', txt(s.labelFormat)],
                    ['Label fetched at', dt(s.labelFetchedAt)],
                    ['POD receiver', txt(s.podReceiverName)],
                    ['POD fetched at', dt(s.podFetchedAt)],
                    ['POD pending', s.podPending == null ? '—' : (s.podPending ? 'yes' : 'no')],
                ]} />
            </Card>

            <Card title="Money">
                <Grid rows={[
                    ['Declared value', money(s.declaredValue, s.declaredValueCurrency)],
                    ['Insured value', money(s.insuredValue, s.declaredValueCurrency)],
                    ['Extra insurance', s.extraInsurance == null ? '—' : (s.extraInsurance ? 'yes' : 'no')],
                    ['COD amount', money(s.codAmount, s.codCurrency)],
                    ['COD collected', money(s.codCollectedAmount, s.codCurrency)],
                    ['COD collected at', dt(s.codCollectedAt)],
                ]} />
            </Card>

            <Card title="Forwarding state, attempts and polling">
                <Grid rows={[
                    ['Status', txt(s.status)],
                    ['Hold reason', txt(s.holdReason, 'not held')],
                    ['Terminal', s.isTerminal == null ? '—' : (s.isTerminal ? 'yes' : 'no')],
                    ['Attempts', `${Number(s.attemptCount || 0)} of ${s.maxAttempts != null ? Number(s.maxAttempts) : '?'}`],
                    ['Last attempt at', dt(s.lastAttemptAt)],
                    ['Next attempt at', dt(s.nextAttemptAt)],
                    ['Last error code', txt(s.lastErrorCode)],
                    ['Last error message', txt(s.lastErrorMsg)],
                    ['Carrier last said', txt(s.lastRemoteStatus, 'nothing')],
                    ['Carrier reason', txt(s.lastRemoteReason)],
                    ['Carrier event at', dt(s.lastRemoteEventAt)],
                    ['Mapped to our status', txt(s.mappedTaskStatus, 'unmapped')],
                    ['Polls run', s.pollAttemptCount != null ? Number(s.pollAttemptCount) : '—'],
                    ['Last polled at', dt(s.lastPolledAt)],
                    ['Next poll at', dt(s.nextPollAt)],
                    ['Forwarded at', dt(s.forwardedAt)],
                    ['Cancel requested at', dt(s.cancelRequestedAt)],
                    ['Amend requested at', dt(s.amendRequestedAt)],
                    ['Cancelled at', dt(s.cancelledAt)],
                    ['Created', dt(s.createdDate)],
                    ['Modified', `${dt(s.modifiedDate)}${s.modifiedDate ? ` (${ago(s.modifiedDate)})` : ''}`],
                ]} />
            </Card>

            <Card title="Consignee">
                {consigneeRestricted && (
                    <Alert tone="info">
                        {txt(consigneeNote, 'Some consignee fields are withheld from this app.')}
                    </Alert>
                )}
                {!consignee ? (
                    <div style={EMPTY}>No consignee details were returned for this leg.</div>
                ) : (
                    <Grid rows={[
                        ['Name', txt(consignee.name)],
                        ['Name (ar)', txt(consignee.nameAr)],
                        ['Phone', txt(consignee.phone)],
                        ['Address', txt(consignee.address)],
                        ['Address (ar)', txt(consignee.addressAr)],
                        ['District', txt(consignee.district)],
                        ['City', txt(consignee.city)],
                        ['Short address', txt(consignee.shortAddress)],
                        ['Postal code', txt(consignee.postalCode)],
                        ['Country', txt(consignee.country)],
                        ['Delivery location type', txt(consignee.deliveryLocationType)],
                        ['Task status (our leg)', txt(consignee.taskStatus)],
                        ['Job type', txt(consignee.jobType)],
                    ]} />
                )}
            </Card>
        </>
    );
}

/**
 * Outbound carrier calls — the OUTCOME of each one, never its body.
 *
 * The request payload, the response body and the target URL are deliberately not
 * rendered anywhere in this app. targetUrl carries our carrier account's API key
 * in its query string, and the payloads carry the whole customer shipment;
 * neither belongs on a screen every driver-app user can reach. Everything that
 * explains WHAT HAPPENED — operation, phase, outcome, HTTP status, error code
 * and message, timings — is here.
 */
function AttemptsTab({ attempts, loading, error }) {
    const rows = Array.isArray(attempts) ? attempts : [];
    return (
        <Card
            title="Outbound attempts (us → carrier)"
            subtitle="Every call this system made to the carrier for this leg, and how each one ended."
        >
            {error && (
                <Alert tone="warn">
                    The detail fetch failed, so this trail may be incomplete or stale: {error}
                </Alert>
            )}

            <div style={{ fontSize: 11, color: '#64748b', marginBottom: 10 }}>
                <i className="fa fa-lock" style={{ marginRight: 6 }} />
                Request and response bodies, and the URL each call was sent to, are not shown here: the carrier URL carries
                our account API key and the bodies carry the customer’s shipment. The outcome of every call is shown in full.
            </div>

            <div style={{ overflowX: 'auto' }}>
                <table style={TABLE}>
                    <thead>
                        <tr>
                            <th style={TH}>#</th>
                            <th style={TH}>Operation</th>
                            <th style={TH}>Phase</th>
                            <th style={TH}>Result</th>
                            <th style={TH}>HTTP</th>
                            <th style={TH}>Error</th>
                            <th style={TH}>Duration</th>
                            <th style={TH}>Source</th>
                            <th style={TH}>Started</th>
                            <th style={TH}>Completed</th>
                        </tr>
                    </thead>
                    <tbody>
                        {loading && <tr><td colSpan={10} style={{ padding: 0 }}><Spinner label="Loading attempts…" /></td></tr>}
                        {!loading && error && (
                            <EmptyRow colSpan={10}>
                                Nothing could be read — the error above explains why. This is <strong>not</strong> an empty trail.
                            </EmptyRow>
                        )}
                        {!loading && !error && rows.length === 0 && (
                            <EmptyRow colSpan={10}>No outbound attempt has been recorded for this leg yet.</EmptyRow>
                        )}
                        {!loading && !error && rows.map((a0, i) => {
                            const a = a0 || {};
                            return (
                                <tr key={`attempt-${txt(a.id, i)}-${i}`}>
                                    <td style={TD}>{a.attemptNo != null ? Number(a.attemptNo) : i + 1}</td>
                                    <td style={{ ...TD, fontWeight: 600 }}>{txt(a.operation)}</td>
                                    <td style={TD}>{txt(a.phase)}</td>
                                    <td style={TD}><OkCell ok={a.ok} okLabel="ok" failLabel="failed" /></td>
                                    <td style={TD}>{a.httpStatus != null ? Number(a.httpStatus) : '—'}</td>
                                    <td style={{ ...TD, color: a.errorCode ? '#dc2626' : '#0f172a', maxWidth: 280 }}>
                                        {a.errorCode ? <strong>{txt(a.errorCode)}</strong> : '—'}
                                        {a.errorMsg ? <div style={{ color: '#991b1b', fontSize: 11 }}>{txt(a.errorMsg)}</div> : null}
                                    </td>
                                    <td style={TD}>{a.durationMs != null ? `${Number(a.durationMs)} ms` : '—'}</td>
                                    <td style={TD}>{txt(a.source)}</td>
                                    <td style={TD}>{dt(a.createdDate)}</td>
                                    <td style={TD}>{dt(a.completedDate)}</td>
                                </tr>
                            );
                        })}
                    </tbody>
                </table>
            </div>
        </Card>
    );
}

function CarrierEventsTab({ events, loading, error, onReload }) {
    const [openRaw, setOpenRaw] = useState({});
    const rows = Array.isArray(events) ? events : [];
    return (
        <Card
            title="Carrier events (carrier → us)"
            subtitle="Inbound status events the carrier pushed, or that we polled, for this leg."
            right={<button onClick={onReload} disabled={loading} style={loading ? BTN.disabled : BTN.ghost}><i className="fa fa-refresh" /> Reload</button>}
        >
            {error && (
                <Alert>
                    Could not load carrier events: {error}
                    <div style={{ marginTop: 4, fontSize: 12 }}>
                        This list is <strong>not empty</strong> — it could not be read. Do not conclude the carrier has said nothing.
                    </div>
                </Alert>
            )}
            <div style={{ overflowX: 'auto', marginTop: error ? 12 : 0 }}>
                <table style={TABLE}>
                    <thead>
                        <tr>
                            <th style={TH}>Received</th>
                            <th style={TH}>Event at</th>
                            <th style={TH}>Carrier</th>
                            <th style={TH}>Remote ref</th>
                            <th style={TH}>Carrier said</th>
                            <th style={TH}>Mapped to</th>
                            <th style={TH}>Outcome</th>
                            <th style={TH}>Signature</th>
                            <th style={TH}>Message</th>
                            <th style={TH}>Details</th>
                        </tr>
                    </thead>
                    <tbody>
                        {loading && <tr><td colSpan={10} style={{ padding: 0 }}><Spinner label="Loading carrier events…" /></td></tr>}
                        {!loading && error && <EmptyRow colSpan={10}>Nothing could be read — see the error above.</EmptyRow>}
                        {!loading && !error && rows.length === 0 && (
                            <EmptyRow colSpan={10}>The carrier has sent no events for this leg.</EmptyRow>
                        )}
                        {!loading && !error && rows.map((e0, i) => {
                            const e = e0 || {};
                            const key = `event-${txt(e.id, i)}-${i}`;
                            const isOpen = !!openRaw[key];
                            return (
                                <React.Fragment key={key}>
                                    <tr>
                                        <td style={TD}>{dt(e.receivedAt)}</td>
                                        <td style={TD}>{dt(e.remoteEventAt)}</td>
                                        <td style={TD}>{txt(e.carrierCode)}</td>
                                        <td style={TD}><code style={{ fontSize: 11 }}>{txt(e.remoteRef)}</code></td>
                                        <td style={{ ...TD, fontWeight: 600, fontFamily: 'monospace' }}>
                                            {txt(e.remoteStatus)}
                                            {e.remoteReason ? <div style={{ fontWeight: 400, fontFamily: 'inherit', color: '#64748b', fontSize: 11 }}>{txt(e.remoteReason)}</div> : null}
                                        </td>
                                        <td style={TD}>
                                            {e.mappedStatus
                                                ? (
                                                    <>
                                                        {txt(e.mappedStatus)}
                                                        {e.mappedRank != null ? <span style={{ color: '#94a3b8' }}> (rank {Number(e.mappedRank)})</span> : null}
                                                    </>
                                                )
                                                : (
                                                    <span style={{ color: '#b45309', fontStyle: 'italic' }} title="No status-mapping row matched this carrier word, so nothing was written to our leg from it.">
                                                        unmapped
                                                    </span>
                                                )}
                                        </td>
                                        <td style={TD}>{txt(e.outcome)}</td>
                                        <td style={TD}>
                                            {e.signatureOk === true && <span style={{ color: '#15803d' }}>ok</span>}
                                            {e.signatureOk === false && <span style={{ color: '#dc2626' }}>bad</span>}
                                            {e.signatureOk == null && <span style={{ color: '#94a3b8' }}>—</span>}
                                        </td>
                                        <td style={{ ...TD, maxWidth: 260 }}>{txt(e.message)}</td>
                                        <td style={TD}>
                                            <button
                                                onClick={() => setOpenRaw((p) => ({ ...p, [key]: !p[key] }))}
                                                style={{ ...BTN.ghost, padding: '4px 9px', fontSize: 11 }}
                                            >
                                                {isOpen ? 'hide' : 'details'}
                                            </button>
                                        </td>
                                    </tr>
                                    {isOpen && (
                                        <tr>
                                            {/* WITHHELD IS NOT THE SAME AS ABSENT. The carrier's raw status
                                                payload and the address it arrived from are stored in
                                                carrier_inbound_event, but the main backend's internal scope
                                                does not emit them to this app, so this panel must not render
                                                them as "not recorded" — that would assert the carrier said
                                                nothing when in fact we simply are not shown what it said. */}
                                            <td colSpan={10} style={{ ...TD, background: '#f8fafc' }}>
                                                <RawBlock label="Direction" value={e.direction} />
                                                <RawBlock label="Mapped status" value={e.mappedStatus} />
                                                <RawBlock label="Outcome" value={e.outcome} />
                                                <RawBlock label="Message" value={e.message} />
                                                <div style={{ fontSize: 11, color: '#64748b', marginTop: 8 }}>
                                                    <i className="fa fa-lock" style={{ marginRight: 6 }} />
                                                    The carrier’s own raw status payload, and the address it was pushed from, are
                                                    <strong> withheld from this app</strong> — not missing. They are recorded against this
                                                    event in the main system and can be read there. The status, reason and mapping above
                                                    are everything this console is given.
                                                </div>
                                            </td>
                                        </tr>
                                    )}
                                </React.Fragment>
                            );
                        })}
                    </tbody>
                </table>
            </div>
        </Card>
    );
}

/**
 * Us → partner, out of webhook_delivery_log.
 *
 * FIELD NAMES ARE ShipmentPartnerEventDto's, not invented ones. The reference is
 * entityRef (with matchedRef saying which of the leg's candidate references it
 * actually matched); the status we told them is eventType; the outcome is ok +
 * httpStatus; the retry machine is retryStatus / retryCount / maxAttempts. There
 * is no "reference", "bookingId", "awb", "success" or "attemptCount" on this DTO,
 * and reading those leaves the column permanently blank.
 *
 * requestPayload / responseBody / targetUrl exist on the DTO and are not shown:
 * this app is read by ~292 users and the payload is the customer's shipment.
 */
function PartnerEventsTab({ payload, loading, error, onReload }) {
    const p = payload || {};
    const rows = Array.isArray(p.events) ? p.events : [];
    const refs = Array.isArray(p.candidateRefs) ? p.candidateRefs : [];
    const exact = p.exact === true;
    return (
        <Card
            title="Partner deliveries (us → partner)"
            subtitle="What the originating partner was actually told about this leg, and how those deliveries were matched to it."
            right={<button onClick={onReload} disabled={loading} style={loading ? BTN.disabled : BTN.ghost}><i className="fa fa-refresh" /> Reload</button>}
        >
            {error && (
                <Alert>
                    Could not load partner deliveries: {error}
                    <div style={{ marginTop: 4, fontSize: 12 }}>
                        This is a read failure, <strong>not</strong> proof that the partner was told nothing.
                    </div>
                </Alert>
            )}

            {!error && !loading && !payload && (
                <div style={EMPTY}>The forwarding service returned nothing for this leg’s partner deliveries.</div>
            )}

            {!error && !loading && payload && (
                <div style={{
                    background: exact ? '#f0f9ff' : '#fef3c7',
                    border: `1px solid ${exact ? '#bae6fd' : '#fde68a'}`,
                    borderRadius: 8, padding: '10px 12px', fontSize: 12,
                    color: exact ? '#0c4a6e' : '#92400e', marginBottom: 12, lineHeight: 1.5,
                }}>
                    <div>
                        <strong>{exact ? 'Exact reference match.' : 'Not an exact match — correlated by reference.'}</strong>{' '}
                        {p.matchStrategy ? <>Strategy <code>{txt(p.matchStrategy)}</code>.</> : null}{' '}
                        {txt(p.matchNote, '')}
                    </div>
                    <div style={{ marginTop: 6 }}>
                        {refs.length > 0
                            ? <>References tried: {refs.map((r, i) => <code key={`ref-${txt(r, i)}-${i}`} style={{ marginRight: 6, fontSize: 11 }}>{txt(r)}</code>)}</>
                            : <>No candidate reference was available for this leg, so nothing could be matched.</>}
                    </div>
                    <div style={{ marginTop: 6, opacity: 0.9 }}>
                        Leg reference <code>{txt(p.legBookingId)}</code> · task booking id <code>{txt(p.taskBookingId)}</code>
                        {p.fkTaskId != null ? <> · task #{Number(p.fkTaskId)}</> : null}
                        {p.fkPartnerId != null ? <> · partner #{Number(p.fkPartnerId)}</> : null}
                    </div>
                    {p.truncated === true ? (
                        <div style={{ marginTop: 6 }}>
                            <i className="fa fa-scissors" style={{ marginRight: 6 }} />
                            Only the most recent {p.limit != null ? Number(p.limit) : ''} of {p.total != null ? Number(p.total) : rows.length} deliveries are shown.
                        </div>
                    ) : null}
                </div>
            )}

            <div style={{ overflowX: 'auto' }}>
                <table style={TABLE}>
                    <thead>
                        <tr>
                            <th style={TH}>When</th>
                            <th style={TH}>Source</th>
                            <th style={TH}>Status we sent</th>
                            <th style={TH}>Reference</th>
                            <th style={TH}>Accepted?</th>
                            <th style={TH}>HTTP</th>
                            <th style={TH}>Retry state</th>
                            <th style={TH}>Message</th>
                        </tr>
                    </thead>
                    <tbody>
                        {loading && <tr><td colSpan={8} style={{ padding: 0 }}><Spinner label="Loading partner deliveries…" /></td></tr>}
                        {!loading && error && <EmptyRow colSpan={8}>Nothing could be read — see the error above.</EmptyRow>}
                        {!loading && !error && rows.length === 0 && (
                            <EmptyRow colSpan={8}>
                                No webhook delivery to this partner could be linked to this leg — as far as this log shows, the
                                partner has been told nothing about it.
                            </EmptyRow>
                        )}
                        {!loading && !error && rows.map((ev0, i) => {
                            const ev = ev0 || {};
                            const when = ev.lastAttemptAt || ev.createdDate;
                            const retries = ev.retryCount != null || ev.maxAttempts != null
                                ? `${ev.retryCount != null ? Number(ev.retryCount) : '?'} / ${ev.maxAttempts != null ? Number(ev.maxAttempts) : '?'}`
                                : null;
                            const message = ev.message || ev.errorMsg || ev.lastErrorCode;
                            return (
                                <tr key={`pe-${txt(ev.id, i)}-${i}`}>
                                    <td style={TD}>
                                        {dt(when)}
                                        {when ? <div style={{ fontSize: 10, color: '#94a3b8' }}>{ago(when)}</div> : null}
                                        {ev.lastAttemptAt && ev.createdDate && ev.lastAttemptAt !== ev.createdDate ? (
                                            <div style={{ fontSize: 10, color: '#94a3b8' }}>first written {dt(ev.createdDate)}</div>
                                        ) : null}
                                    </td>
                                    <td style={TD}>{txt(ev.source)}</td>
                                    <td style={{ ...TD, fontWeight: 600 }}>
                                        {ev.eventType ? <StatusPill status={ev.eventType} /> : <UnknownPill label="not recorded" />}
                                        {ev.eventCode || ev.exceptionCode ? (
                                            <div style={{ fontSize: 10, fontWeight: 400, color: '#94a3b8', marginTop: 3 }}>
                                                {ev.eventCode ? `code ${txt(ev.eventCode)}` : ''}
                                                {ev.eventCode && ev.exceptionCode ? ' · ' : ''}
                                                {ev.exceptionCode ? `exception ${txt(ev.exceptionCode)}` : ''}
                                            </div>
                                        ) : null}
                                        {ev.cancelReason ? (
                                            <div style={{ fontSize: 10, fontWeight: 400, color: '#94a3b8' }}>cancel reason {txt(ev.cancelReason)}</div>
                                        ) : null}
                                    </td>
                                    <td style={TD}>
                                        <code style={{ fontSize: 11 }}>{txt(ev.entityRef)}</code>
                                        <div style={{ fontSize: 10, color: '#94a3b8', marginTop: 2 }}>
                                            {ev.entityType ? `${txt(ev.entityType)} · ` : ''}
                                            matched on {ev.matchedRef ? <code style={{ fontSize: 10 }}>{txt(ev.matchedRef)}</code> : '—'}
                                        </div>
                                    </td>
                                    <td style={TD}>
                                        <OkCell ok={ev.ok} okLabel="accepted" failLabel="rejected" nullLabel="never completed" />
                                    </td>
                                    <td style={TD}>{ev.httpStatus != null ? Number(ev.httpStatus) : '—'}</td>
                                    <td style={TD}>
                                        {txt(ev.retryStatus)}
                                        {retries ? <div style={{ fontSize: 10, color: '#94a3b8' }}>attempt {retries}</div> : null}
                                        {ev.nextRetryAt ? <div style={{ fontSize: 10, color: '#94a3b8' }}>next {dt(ev.nextRetryAt)}</div> : null}
                                    </td>
                                    <td style={{ ...TD, maxWidth: 300, color: '#64748b' }}>{txt(message)}</td>
                                </tr>
                            );
                        })}
                    </tbody>
                </table>
            </div>
        </Card>
    );
}

function LabelTab({ meta, metaLoading, metaLoaded, metaError, onLoadMeta, url, blobLoading, blobError, onView }) {
    const available = meta && meta.available === true;
    return (
        <Card
            title="Shipping label"
            subtitle="Streamed through the driver backend. The carrier's own label URL carries an API key and never reaches this browser."
            right={(
                <button onClick={onLoadMeta} disabled={metaLoading} style={metaLoading ? BTN.disabled : BTN.ghost}>
                    <i className={`fa fa-refresh ${metaLoading ? 'fa-spin' : ''}`} /> {metaLoaded ? 'Recheck' : 'Check'}
                </button>
            )}
        >
            {metaLoading && <Spinner label="Checking for a label…" />}

            {!metaLoading && metaError && (
                <Alert>
                    Could not check whether a label exists: {metaError}
                    <div style={{ marginTop: 4, fontSize: 12 }}>
                        This says nothing about whether a label exists — only that the check failed.
                    </div>
                </Alert>
            )}

            {!metaLoading && !metaError && !metaLoaded && (
                <div style={EMPTY}>Checking for this leg’s label…</div>
            )}

            {!metaLoading && !metaError && metaLoaded && !meta && (
                <div style={EMPTY}>No label information was returned for this leg.</div>
            )}

            {!metaLoading && !metaError && meta && !available && (
                <Alert tone="info">
                    No label is available for this leg.
                    <div style={{ marginTop: 4 }}>{txt(meta.reason, 'The server gave no reason.')}</div>
                </Alert>
            )}

            {!metaLoading && !metaError && meta && available && (
                <>
                    <div style={{ display: 'flex', gap: 20, flexWrap: 'wrap', fontSize: 12, color: '#475569', marginBottom: 6 }}>
                        <span><strong>Carrier:</strong> {txt(meta.carrierCode)}</span>
                        <span><strong>AWB:</strong> {txt(meta.carrierHawb)}</span>
                        <span><strong>Leg:</strong> {txt(meta.legBookingId)}</span>
                        <span><strong>Format:</strong> {txt(meta.format)}</span>
                        <span><strong>File:</strong> {txt(meta.filename)}</span>
                        <span><strong>Source:</strong> {txt(meta.source)}</span>
                    </div>
                    {meta.sourceNote ? (
                        <div style={{ fontSize: 11, color: '#94a3b8', marginBottom: 12 }}>{txt(meta.sourceNote)}</div>
                    ) : null}

                    <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
                        <button onClick={onView} disabled={blobLoading} style={blobLoading ? BTN.disabled : BTN.primary}>
                            {blobLoading
                                ? <><i className="fa fa-spinner fa-spin" /> Loading label…</>
                                : <><i className="fa fa-eye" /> {url ? 'Reload label' : 'View label'}</>}
                        </button>
                        {url && (
                            <a href={url} download={txt(meta.filename, 'label')} style={{ ...BTN.ghost, textDecoration: 'none' }}>
                                <i className="fa fa-download" /> Download
                            </a>
                        )}
                    </div>

                    {blobError && <Alert>Could not load the label file: {blobError}</Alert>}

                    {url && (
                        <div style={{ marginTop: 14, border: '1px solid #e2e8f0', borderRadius: 8, overflow: 'hidden' }}>
                            <iframe src={url} title="Shipping label" style={{ width: '100%', height: 620, border: 'none' }} />
                        </div>
                    )}
                </>
            )}
        </Card>
    );
}

// ── small helpers ───────────────────────────────────────────────────────────

const PAGE = { padding: 20, background: '#f8fafc', minHeight: '100vh' };
const EMPTY = { padding: 18, textAlign: 'center', color: '#94a3b8', fontSize: 13 };
const TABLE = { width: '100%', borderCollapse: 'collapse', fontSize: 12 };

const numOr = (v, fb = '—') => (v === null || v === undefined || isNaN(Number(v)) ? fb : Number(v));

/** true / false / null rendered as three distinct things. Null is never "false". */
function OkCell({ ok, okLabel, failLabel, nullLabel }) {
    if (ok === true) return <span style={{ color: '#15803d', fontWeight: 600 }}>{okLabel || 'ok'}</span>;
    if (ok === false) return <span style={{ color: '#dc2626', fontWeight: 600 }}>{failLabel || 'failed'}</span>;
    return <span style={{ color: '#94a3b8' }}>{nullLabel || 'unknown'}</span>;
}

function BackLink({ navigate }) {
    return (
        <button onClick={() => navigate('/orders/forwarded-shipments')} style={{ ...BTN.ghost, marginBottom: 14 }}>
            <i className="fa fa-arrow-left" /> Back to forwarded orders
        </button>
    );
}

function Tag({ children, tone }) {
    const bg = tone === 'warn' ? '#fef3c7' : '#f1f5f9';
    const fg = tone === 'warn' ? '#b45309' : '#475569';
    return (
        <span style={{ padding: '2px 8px', borderRadius: 10, fontSize: 10, fontWeight: 600, background: bg, color: fg }}>
            {children}
        </span>
    );
}

function Grid({ rows }) {
    const list = Array.isArray(rows) ? rows : [];
    if (list.length === 0) return <div style={EMPTY}>Nothing to show.</div>;
    return (
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(240px, 1fr))', gap: '10px 20px' }}>
            {list.map(([label, value], i) => (
                <div key={`${label}-${i}`} style={{ borderBottom: '1px solid #f1f5f9', paddingBottom: 6 }}>
                    <div style={{ fontSize: 10, fontWeight: 600, color: '#64748b', textTransform: 'uppercase', letterSpacing: '.04em' }}>{label}</div>
                    <div style={{ fontSize: 13, color: '#0f172a', marginTop: 2, wordBreak: 'break-word' }}>
                        {value === null || value === undefined || value === '' ? '—' : value}
                    </div>
                </div>
            ))}
        </div>
    );
}

function RawBlock({ label, value }) {
    if (value === null || value === undefined || String(value).trim() === '') {
        return (
            <div style={{ marginBottom: 8 }}>
                <div style={{ fontSize: 10, fontWeight: 600, color: '#64748b', textTransform: 'uppercase' }}>{label}</div>
                <div style={{ fontSize: 12, color: '#94a3b8' }}>not recorded</div>
            </div>
        );
    }
    let text = String(value);
    try {
        text = JSON.stringify(JSON.parse(text), null, 2);
    } catch (_) { /* not JSON — show it raw */ }
    return (
        <div style={{ marginBottom: 8 }}>
            <div style={{ fontSize: 10, fontWeight: 600, color: '#64748b', textTransform: 'uppercase' }}>{label}</div>
            <pre style={{
                margin: '4px 0 0', padding: 10, background: '#0f172a', color: '#e2e8f0', borderRadius: 6,
                fontSize: 11, maxHeight: 280, overflow: 'auto', whiteSpace: 'pre-wrap', wordBreak: 'break-all',
            }}>
                {text}
            </pre>
        </div>
    );
}
